import React, { createContext, useContext, useEffect, useRef } from "react";
import { io } from "socket.io-client";
import { useAuth } from "./AuthContext";
import { useChat } from "./ChatContext";
// NOTE: toast import removed — notifications are system-level only (via Service Worker)
import { useSystemNotifications } from "../hooks/useSystemNotifications";
import { usePushSubscription } from "../hooks/usePushSubscription";

const SocketContext = createContext();
const NotifPermissionContext = createContext({
  permissionState: "default",
  requestPermission: async () => {},
});

export const useSocket = () => useContext(SocketContext);
export const useNotifPermission = () => useContext(NotifPermissionContext);

export const SocketProvider = ({ children }) => {
  const { user } = useAuth();
  const {
    setDmUnreadCounts,
    setChannelUnreadCounts,
    setTotalUnread,
    setDirectMessages,
    refreshData,
    setOnlineUsers,
    activeChat,
    lastReadAt,
    setLastReadAt,
  } = useChat();
  const socket = useRef(null);
  const seenNotifIds = useRef(new Set());
  // Keep stable refs so socket listeners always read the latest values
  const refreshDataRef = useRef(refreshData);
  const activeChatRef = useRef(activeChat);
  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);
  useEffect(() => {
    activeChatRef.current = activeChat;
  }, [activeChat]);
  const lastReadAtRef = useRef(lastReadAt);
  useEffect(() => {
    lastReadAtRef.current = lastReadAt;
  }, [lastReadAt]);

  // ── System notifications (Web Notifications API) ──────────────────────────
  const pushHook = usePushSubscription();
  const userId = user?._id || user?.id || null;
  const { showNotification, permissionState, requestPermission } =
    useSystemNotifications(activeChatRef, pushHook, userId);

  useEffect(() => {
    const userId = user?._id || user?.id;
    if (!userId) return;

    // ── Robust socket URL: extract origin only, works for any URL shape ──
    // "https://api-portal.sapientcodelabs.xyz/api/" → "https://api-portal.sapientcodelabs.xyz"
    // "http://localhost:1338/api/"                  → "http://localhost:1338"
    const rawBackendUrl =
      process.env.REACT_APP_BACKEND_URL || "http://localhost:1338/api/";
    let socketUrl;
    try {
      socketUrl = new URL(rawBackendUrl).origin;
    } catch {
      // Malformed URL — strip path manually as fallback
      socketUrl = rawBackendUrl.replace(/\/api\/?.*$/, "").replace(/\/$/, "");
      console.warn(
        "[SocketContext] Could not parse REACT_APP_BACKEND_URL, using fallback:",
        socketUrl,
      );
    }

    console.log(
      "SocketContext | trying to connect to:",
      socketUrl,
      "| userId:",
      userId,
    );

    socket.current = io(socketUrl, {
      // Start with websocket directly — skip polling entirely.
      // This is the correct production setting when your server/proxy
      // supports WebSocket upgrades (nginx with proxy_http_version 1.1
      // and Upgrade/Connection headers set).
      // Polling is kept as fallback ONLY — socket.io tries websocket first,
      // falls back to polling if websocket fails, then upgrades back.
      transports: ["websocket", "polling"],
      upgrade: true,
      withCredentials: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
      // ngrok free tier shows a browser warning page for unrecognized requests.
      // This header tells ngrok to skip it and forward directly to the server.
      // extraHeaders: {
      //     "ngrok-skip-browser-warning": "true",
      // },
    });

    socket.current.on("connect", () => {
      const transport = socket.current.io.engine.transport.name;
      console.log(
        "SocketContext | connected | socket id:",
        socket.current.id,
        "| transport:",
        transport,
      );
      socket.current.emit("register", userId);

      // Listen for transport upgrade (polling → websocket)
      socket.current.io.engine.on("upgrade", (newTransport) => {
        console.log(
          "SocketContext | transport upgraded to:",
          newTransport.name,
        );
      });

      if (pushHook?.subscribeToPush && userId) {
        pushHook.subscribeToPush(userId);
      }
    });

    socket.current.on("connect_error", (err) => {
      console.error(
        "SocketContext | connection failed:",
        err.message,
        "| url was:",
        socketUrl,
      );
    });

    socket.current.on("disconnect", (reason) => {
      console.warn("SocketContext | disconnected, reason:", reason);
    });

    socket.current.on("reconnect_attempt", (attempt) => {
      console.log("SocketContext | reconnect attempt #" + attempt);
    });

    socket.current.on("reconnect_failed", () => {
      console.error(
        "SocketContext | all reconnect attempts failed, socket is dead",
      );
    });

    // Re-register user after reconnect so the server re-joins the personal room
    socket.current.on("reconnect", (attempt) => {
      console.log(
        "SocketContext | reconnected after",
        attempt,
        "attempt(s), re-registering userId:",
        userId,
      );
      socket.current.emit("register", userId);
    });

    // Listen for DM unread count updates (Strictly isolated from channels)
    socket.current.on("dmUnreadUpdate", (res) => {
      if (res?.userId === userId && Array.isArray(res.data)) {
        const data = res.data;
        const currentChat = activeChatRef.current;
        const lastReadMap = lastReadAtRef.current || {};

        setDmUnreadCounts((prev) => {
          const merged = { ...prev };
          data.forEach((stat) => {
            const contactId = stat.otherUserId;
            const count = stat.unreadCount || 0;

            const isViewing =
              currentChat?.type === "dm" &&
              (currentChat?.data?._id || currentChat?.data?.id)?.toString() ===
                contactId;

            const lastRead = lastReadMap[contactId] || 0;
            const isRecentlyRead = Date.now() - lastRead < 2000;

            if (!isViewing && !isRecentlyRead) {
              const oldCount = prev[contactId] || 0;
              if (count > oldCount) {
                merged[contactId] = count;
                setTotalUnread((prevTotal) => prevTotal + (count - oldCount));
              }
            } else if (isViewing || isRecentlyRead) {
              if (merged[contactId] !== 0) {
                const diff = merged[contactId] || 0;
                setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
                merged[contactId] = 0;
              }
            }
          });
          return merged;
        });
      }
    });

    // Listen for data updates in channels
    // socket.current.on("dataUpdate-in-channel", (res) => {
    //     if (res?.userId === userId) {
    //         const data = res?.data || {};
    //         setChannelUnreadCounts(prev => ({ ...prev, ...data }));
    //     }
    // });

    // Listen for data updates in channels (Isolated from DMs)
    socket.current.on("dataUpdate-in-channel", (res) => {
      if (res?.userId === userId) {
        const data = res?.data || {};
        const currentChat = activeChatRef.current;
        const lastReadMap = lastReadAtRef.current || {};
        setChannelUnreadCounts((prev) => {
          const merged = { ...prev };
          Object.entries(data).forEach(([channelId, count]) => {
            const isViewing =
              currentChat?.type === "channel" &&
              (currentChat?.data?._id || currentChat?.data?.id)?.toString() ===
                channelId;

            const lastRead = lastReadMap[channelId] || 0;
            const isRecentlyRead = Date.now() - lastRead < 2000;

            if (!isViewing && !isRecentlyRead) {
              const oldCount = prev[channelId] || 0;
              const newCount = count || 0;
              // STRICT: Only update if it's an increase or we are sure it's not stale
              if (newCount > oldCount) {
                merged[channelId] = newCount;
                setTotalUnread((prevTotal) => prevTotal + (newCount - oldCount));
              }
            } else if (isViewing || isRecentlyRead) {
              if (merged[channelId] !== 0) {
                const diff = merged[channelId] || 0;
                setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
                merged[channelId] = 0;
              }
            }
          });
          return merged;
        });
      }
    });
    //=============================================

    // Sync unread counts for specific channels
    socket.current.on("channelUnreadUpdate", (data) => {
      const { channelId, unreadCount, lastReadAt: remoteReadAt } = data || {};
      if (!channelId) return;

      const localTs = lastReadAtRef.current?.[channelId] || 0;
      const remoteTs = remoteReadAt ? new Date(remoteReadAt).getTime() : 0;

      // STRICT: Ignore stale server updates
      if (remoteTs < localTs) return;
      if (Date.now() - localTs < 2000) return;

      setChannelUnreadCounts((prev) => {
        const oldCount = prev[channelId] || 0;
        const newCount = unreadCount || 0;
        const diff = newCount - oldCount;

        if (diff !== 0) {
          setTotalUnread((prevTotal) => Math.max(0, prevTotal + diff));
        }
        return { ...prev, [channelId]: newCount };
      });
    });

    // ── Multi-tab sync: reset unread counts when read in another tab ──────
    socket.current.on("channel:read", ({ channelId, lastReadAt: remoteReadAt }) => {
      if (remoteReadAt) {
        setLastReadAt((prev) => {
          const t = new Date(remoteReadAt).getTime();
          if (prev[channelId] && prev[channelId] >= t) return prev;
          return { ...prev, [channelId]: t };
        });
      }
      setChannelUnreadCounts((prev) => {
        if (!prev[channelId]) return prev;
        const diff = prev[channelId];
        setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
        return { ...prev, [channelId]: 0 };
      });
    });

    socket.current.on("dm:read", ({ contactId, lastReadAt: remoteReadAt }) => {
      if (remoteReadAt) {
        setLastReadAt((prev) => {
          const t = new Date(remoteReadAt).getTime();
          if (prev[contactId] && prev[contactId] >= t) return prev;
          return { ...prev, [contactId]: t };
        });
      }
      setDmUnreadCounts((prev) => {
        if (!prev[contactId]) return prev;
        const diff = prev[contactId];
        setTotalUnread((prevTotal) => Math.max(0, prevTotal - diff));
        return { ...prev, [contactId]: 0 };
      });
    });

    // Listen for real-time channel membership updates
    socket.current.on("channel:member_added", (data) => {
      if (data?.memberId?.toString() === userId.toString()) {
        console.log(
          `[SocketContext] 📥 You were added to a channel (${data.channelId}), refreshing data...`,
        );
        refreshDataRef.current();
      }
    });

    socket.current.on("channel:member_removed", (data) => {
      if (data?.memberId?.toString() === userId.toString()) {
        console.log(
          `[SocketContext] 📤 You were removed from a channel (${data.channelId}), refreshing data...`,
        );
        refreshDataRef.current();
      }
    });

    // Listen for real-time channel creation
    socket.current.on("channel:created", (channel) => {
      if (!channel || !channel.members) return;
      const isMember = channel.members.some(
        (mId) => (typeof mId === "string" ? mId : mId._id || mId.id) === userId,
      );
      if (isMember) {
        console.log(
          `[SocketContext] 📢 New channel created (#${channel.name}), refreshing data...`,
        );
        refreshDataRef.current();
      }
    });

    // Real-time presence: track who is online
    socket.current.on("presenceUpdate", (onlineUserIds) => {
      setOnlineUsers(onlineUserIds || []);
    });

    // ── Real-time notifications ───────────────────────────────────────────
    // Fires when someone sends a DM or channel message to this user.
    // If the user is already viewing that chat, suppress the toast but
    // still increment the unread count (it will be reset by markChatAsRead).
    // socket.current.on("notification:receive", (notif) => {
    //   const { chatType, chatId, senderName, messagePreview, channelName } =
    //     notif;

    //   console.log(
    //     "SocketContext | notification:receive fired | from:",
    //     senderName,
    //     "| chatType:",
    //     chatType,
    //     "| chatId:",
    //     chatId,
    //     "| preview:",
    //     (messagePreview || "").slice(0, 40),
    //   );

    //   if (!chatId) {
    //     console.warn(
    //       "SocketContext | notification:receive dropped — chatId is missing in payload",
    //     );
    //     return;
    //   }

    //   const currentChat = activeChatRef.current;
    //   const isViewingThisChat =
    //     currentChat?.type === (chatType === "direct" ? "dm" : "channel") &&
    //     (currentChat?.data?._id || currentChat?.data?.id)?.toString() ===
    //       chatId.toString();

    //   console.log(
    //     "SocketContext | notification:receive | user currently viewing this chat?",
    //     isViewingThisChat,
    //   );

    //   // Always increment unread count unless user is actively viewing the chat
    //   if (!isViewingThisChat) {
    //     if (chatType === "direct") {
    //       setDmUnreadCounts((prev) => {
    //         const current = prev[chatId] || 0;
    //         const next = { ...prev, [chatId]: current + 1 };
    //         setTotalUnread((prevTotal) => prevTotal + 1);
    //         return next;
    //       });
    //     } else if (chatType === "channel") {
    //       setChannelUnreadCounts((prev) => {
    //         const current = prev[chatId] || 0;
    //         const next = { ...prev, [chatId]: current + 1 };
    //         setTotalUnread((prevTotal) => prevTotal + 1);
    //         return next;
    //       });
    //     }
    //   }

    //   // ── System notification (Web Notifications API) ───────────────────
    //   // The hook handles its own suppression + dedup guards internally.

    //   //============= [NOTIF DEBUG] Before calling showNotification (system notification)
    //   console.log(
    //     "[NOTIF][CLIENT] Calling showNotification — permission:",
    //     typeof Notification !== "undefined"
    //       ? Notification.permission
    //       : "API unavailable",
    //   );
    //   //=============

    //   showNotification(notif);

    //   //============= [NOTIF DEBUG] After calling showNotification
    //   console.log("[NOTIF][CLIENT] showNotification called ✅");
    //   //=============
    // });

    //===================================
    socket.current.on("notification:receive", (notif) => {
      const { chatType, chatId, messageId, senderName, messagePreview } = notif;

      console.log(
        "SocketContext | notification:receive | from:",
        senderName,
        "| chatType:",
        chatType,
        "| chatId:",
        chatId,
        "| messageId:",
        messageId,
      );

      // Guard 1: chatId required
      if (!chatId) {
        console.warn(
          "SocketContext | notification:receive dropped — missing chatId",
        );
        return;
      }

      // Guard 2: chatType must be known — strict isolation starts here
      if (chatType !== "direct" && chatType !== "channel") {
        console.warn(
          "SocketContext | notification:receive dropped — unknown chatType:",
          chatType,
        );
        return;
      }

      // Guard 3: messageId deduplication — prevents double-increment on reconnect/replay
      if (messageId) {
        const dedupKey = `${chatType}:${chatId}:${messageId}`;
        if (seenNotifIds.current.has(dedupKey)) {
          console.log(
            "SocketContext | notification:receive deduped:",
            dedupKey,
          );
          return;
        }
        seenNotifIds.current.add(dedupKey);
        // Prevent unbounded memory growth — keep last 1000 keys (FIFO)
        if (seenNotifIds.current.size > 1000) {
          seenNotifIds.current.delete(
            seenNotifIds.current.values().next().value,
          );
        }
      }

      // Guard 4: suppress badge if user is actively viewing this chat
      const currentChat = activeChatRef.current;
      const mappedType = chatType === "direct" ? "dm" : "channel";
      const isViewingThisChat =
        currentChat?.type === mappedType &&
        (currentChat?.data?._id || currentChat?.data?.id)?.toString() ===
          chatId.toString();

      console.log(
        "SocketContext | notification:receive | isViewingThisChat:",
        isViewingThisChat,
      );

      // Guard 5: Ignore if message is older than the last time we marked this chat as read
      const lastReadTs = lastReadAtRef.current?.[chatId] || 0;
      const msgTs = new Date(notif.timestamp || Date.now()).getTime();
      if (msgTs <= lastReadTs || Date.now() - lastReadTs < 2000) {
        console.log(
          "SocketContext | notification:receive ignored — stale message or recently read",
        );
        return;
      }

      if (!isViewingThisChat) {
        if (chatType === "direct") {
          // ✅ STRICT: only dmUnreadCounts updated — channelUnreadCounts NOT touched
          setDmUnreadCounts((prev) => {
            const next = { ...prev, [chatId]: (prev[chatId] || 0) + 1 };
            setTotalUnread((prevTotal) => prevTotal + 1);
            return next;
          });
        } else if (chatType === "channel") {
          // ✅ STRICT: only channelUnreadCounts updated — dmUnreadCounts NOT touched
          setChannelUnreadCounts((prev) => {
            const next = { ...prev, [chatId]: (prev[chatId] || 0) + 1 };
            setTotalUnread((prevTotal) => prevTotal + 1);
            return next;
          });
        }
      }

      // System notification
      console.log(
        "[NOTIF][CLIENT] Calling showNotification — permission:",
        typeof Notification !== "undefined"
          ? Notification.permission
          : "API unavailable",
      );
      showNotification(notif);
      console.log("[NOTIF][CLIENT] showNotification called ✅");
    });
    //===================================
    return () => {
      if (socket.current) {
        socket.current.disconnect();
      }
    };
    // ⚠️ refreshData intentionally excluded — we use refreshDataRef above.
    // Including it would reconnect the socket every time refreshData changes.
  }, [
    user?._id,
    user?.id,
    setDmUnreadCounts,
    setChannelUnreadCounts,
    setTotalUnread,
    setDirectMessages,
    setOnlineUsers,
  ]);

  return (
    <SocketContext.Provider value={socket.current}>
      <NotifPermissionContext.Provider
        value={{ permissionState, requestPermission }}
      >
        {children}
      </NotifPermissionContext.Provider>
    </SocketContext.Provider>
  );
};

//===============================================
//===============================================

// import React, { createContext, useContext, useEffect, useRef } from "react";
// import { io } from "socket.io-client";
// import { useAuth } from "./AuthContext";
// import { useChat } from "./ChatContext";
// import { useSystemNotifications } from "../hooks/useSystemNotifications";
// import { usePushSubscription } from "../hooks/usePushSubscription";

// const SocketContext = createContext();
// const NotifPermissionContext = createContext({
//     permissionState: "default",
//     requestPermission: async () => {},
// });

// export const useSocket = () => useContext(SocketContext);
// export const useNotifPermission = () => useContext(NotifPermissionContext);

// // ── Root cause fix #1: Robust socket URL derivation ───────────────────────────
// // The original code did:
// //   backendUrl.replace("/api/", "/").replace("/api", "")
// // which fails whenever REACT_APP_BACKEND_URL has a different shape in production
// // (e.g. "https://api.yourdomain.com/api" → leaves "https://api.yourdomain.com/"
// //  but "https://yourdomain.com" → "wss://https/..." which is the exact crash).
// //
// // The correct approach: use the URL constructor to extract just origin.
// // This works regardless of path, trailing slash, or http vs https.
// //   "https://api.yourdomain.com/api/"  → "https://api.yourdomain.com"
// //   "http://localhost:1338/api/"       → "http://localhost:1338"
// //   "https://yourdomain.com"           → "https://yourdomain.com"
// function deriveSocketUrl(backendUrl) {
//     try {
//         // URL constructor requires an absolute URL
//         const url = new URL(backendUrl);
//         // Return only the origin (scheme + host + port) — no path, no trailing slash
//         return url.origin;
//     } catch {
//         // backendUrl is malformed — fall back to localhost for dev
//         console.error(
//             "[SocketContext] REACT_APP_BACKEND_URL is not a valid URL:",
//             backendUrl,
//             "— falling back to http://localhost:1338"
//         );
//         return "http://localhost:1338";
//     }
// }

// export const SocketProvider = ({ children }) => {
//     const { user } = useAuth();
//     const {
//         setUnreadCounts,
//         setTotalUnread,
//         setDirectMessages,
//         refreshData,
//         setOnlineUsers,
//         activeChat,
//     } = useChat();

//     const socket = useRef(null);

//     // Stable refs — socket listeners always read the latest values without
//     // causing the socket effect to re-run on every render.
//     const refreshDataRef = useRef(refreshData);
//     const activeChatRef = useRef(activeChat);
//     useEffect(() => { refreshDataRef.current = refreshData; }, [refreshData]);
//     useEffect(() => { activeChatRef.current = activeChat; }, [activeChat]);

//     // System notifications
//     const pushHook = usePushSubscription();
//     const userId = user?._id || user?.id || null;
//     const { showNotification, permissionState, requestPermission } =
//         useSystemNotifications(activeChatRef, pushHook, userId);

//     useEffect(() => {
//         const uid = user?._id || user?.id;
//         if (!uid) return;

//         // ── Root cause fix #1: correct socket URL ─────────────────────────────
//         const rawBackendUrl =
//             process.env.REACT_APP_BACKEND_URL || "http://localhost:1338/api/";
//         const socketUrl = deriveSocketUrl(rawBackendUrl);

//         console.log("[SocketContext] Connecting socket to:", socketUrl);

//         // ── Root cause fix #2: transport fallback for production ──────────────
//         // In production, many reverse proxies (nginx, Cloudflare, AWS ALB) block
//         // raw WebSocket upgrades or need extra headers. Using ["polling", "websocket"]
//         // lets socket.io start with a working long-poll handshake and then upgrade.
//         // This is why messages were "stuck" locally but slow in production:
//         // the websocket-only transport was silently failing on the first connect,
//         // causing reconnection loops before a message could be delivered.
//         //
//         // If your production nginx/proxy is confirmed to support WebSocket upgrades,
//         // you can revert to ["websocket"] — but ["polling", "websocket"] is the
//         // safe production default and has negligible overhead after the upgrade.
//         socket.current = io(socketUrl, {
//             transports: ["polling", "websocket"],
//             upgrade: true,                  // upgrade from polling → websocket once connected
//             reconnectionAttempts: 10,       // more retries in production
//             reconnectionDelay: 1000,        // 1s initial delay
//             reconnectionDelayMax: 5000,     // cap at 5s
//             timeout: 20000,
//             withCredentials: true,          // send cookies (needed if backend uses cookie auth)
//         });

//         socket.current.on("connect", () => {
//             console.log("[SocketContext] ✅ Socket connected:", socket.current.id);
//             socket.current.emit("register", uid);
//             if (pushHook?.subscribeToPush && uid) {
//                 pushHook.subscribeToPush(uid);
//             }
//         });

//         socket.current.on("connect_error", (err) => {
//             // Log the full error so it's diagnosable in production logs
//             console.error("[SocketContext] ❌ Socket connection error:", err.message, {
//                 transport: socket.current?.io?.engine?.transport?.name,
//                 socketUrl,
//             });
//         });

//         socket.current.on("reconnect_attempt", (attempt) => {
//             console.log("[SocketContext] 🔄 Reconnect attempt #", attempt);
//         });

//         socket.current.on("reconnect_failed", () => {
//             console.error("[SocketContext] ❌ Reconnect FAILED — all attempts exhausted.");
//         });

//         socket.current.on("reconnect", () => {
//             // Re-register user after reconnect so server knows this socket belongs to uid
//             console.log("[SocketContext] ✅ Reconnected — re-registering user:", uid);
//             socket.current.emit("register", uid);
//         });

//         // ── Data sync events ──────────────────────────────────────────────────

//         socket.current.on("dataUpdate", (res) => {
//             if (res?.userId === uid) {
//                 refreshDataRef.current();
//             }
//         });

//         socket.current.on("dataUpdate-in-channel", (res) => {
//             if (res?.userId === uid) {
//                 const data = res?.data || {};
//                 setUnreadCounts((prev) => ({ ...prev, ...data }));
//             }
//         });

//         socket.current.on("channelUnreadUpdate", (data) => {
//             if (data?.channelId) {
//                 setUnreadCounts((prev) => ({
//                     ...prev,
//                     [data.channelId]: data.unreadCount,
//                 }));
//             }
//         });

//         socket.current.on("channel:member_added", (data) => {
//             if (data?.memberId?.toString() === uid.toString()) {
//                 refreshDataRef.current();
//             }
//         });

//         socket.current.on("channel:member_removed", (data) => {
//             if (data?.memberId?.toString() === uid.toString()) {
//                 refreshDataRef.current();
//             }
//         });

//         socket.current.on("channel:created", (channel) => {
//             if (!channel?.members) return;
//             const isMember = channel.members.some(
//                 (mId) =>
//                     (typeof mId === "string" ? mId : mId._id || mId.id) === uid
//             );
//             if (isMember) refreshDataRef.current();
//         });

//         socket.current.on("presenceUpdate", (onlineUserIds) => {
//             setOnlineUsers(onlineUserIds || []);
//         });

//         // ── Notification events ───────────────────────────────────────────────

//         socket.current.on("notification:receive", (notif) => {
//             const { chatType, chatId } = notif;

//             if (!chatId) return;

//             const currentChat = activeChatRef.current;
//             const isViewingThisChat =
//                 currentChat?.type === (chatType === "direct" ? "dm" : "channel") &&
//                 (currentChat?.data?._id || currentChat?.data?.id)?.toString() ===
//                     chatId.toString();

//             if (!isViewingThisChat) {
//                 setUnreadCounts((prev) => {
//                     const next = { ...prev, [chatId]: (prev[chatId] || 0) + 1 };
//                     setTotalUnread(
//                         Object.values(next).reduce((s, v) => s + (v || 0), 0)
//                     );
//                     return next;
//                 });
//             }

//             showNotification(notif);
//         });

//         return () => {
//             if (socket.current) {
//                 socket.current.disconnect();
//                 socket.current = null;
//             }
//         };
//     }, [user?._id, user?.id, setUnreadCounts, setTotalUnread, setDirectMessages, setOnlineUsers]);
//     // ⚠️ refreshData excluded intentionally — accessed via refreshDataRef

//     return (
//         <SocketContext.Provider value={socket.current}>
//             <NotifPermissionContext.Provider
//                 value={{ permissionState, requestPermission }}
//             >
//                 {children}
//             </NotifPermissionContext.Provider>
//         </SocketContext.Provider>
//     );
// };
